// WordPress Classic Editor Utilities
// Dựa trên WordPress source code để implement các tính năng bổ sung

/**
 * Word Counter - Similar to WordPress wp.utils.WordCounter
 */
export class WordCounter {
  constructor() {
    this.settings = {
      strip: /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      clean: /<!--[\s\S]*?-->|<\?(?:php)?[\s\S]*?\?>/gi,
      space: /&nbsp;|&#160;/gi,
      remove: /<(?:[^>=]|='[^']*'|="[^"]*"|=[^'"][^\s>]*)*>/gi
    };
  }

  count(text) {
    if (!text) return 0;
    
    // Remove scripts and comments
    text = text.replace(this.settings.strip, '');
    text = text.replace(this.settings.clean, '');
    
    // Convert &nbsp; to regular spaces
    text = text.replace(this.settings.space, ' ');
    
    // Remove HTML tags
    text = text.replace(this.settings.remove, '');
    
    // Decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    text = textarea.value;
    
    // Count words
    text = text.trim();
    if (!text) return 0;
    
    return text.split(/\s+/).length;
  }
}

/**
 * Auto-save functionality - Similar to WordPress autosave
 */
export class AutoSave {
  constructor(options = {}) {
    this.options = {
      interval: 60000, // 1 minute
      storage: 'localStorage',
      prefix: 'autosave_',
      ...options
    };
    this.timers = new Map();
  }

  save(key, content) {
    const data = {
      content,
      timestamp: Date.now(),
      id: key
    };
    
    if (this.options.storage === 'localStorage') {
      localStorage.setItem(
        this.options.prefix + key, 
        JSON.stringify(data)
      );
    }
  }

  restore(key) {
    if (this.options.storage === 'localStorage') {
      const saved = localStorage.getItem(this.options.prefix + key);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error('Error parsing autosave data:', e);
        }
      }
    }
    return null;
  }

  schedule(key, content) {
    // Clear existing timer
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
    }

    // Schedule new save
    const timer = setTimeout(() => {
      this.save(key, content);
      console.log(`Auto-saved content for ${key}`);
    }, this.options.interval);

    this.timers.set(key, timer);
  }

  clear(key) {
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
    }
    
    if (this.options.storage === 'localStorage') {
      localStorage.removeItem(this.options.prefix + key);
    }
  }
}

/**
 * Image Upload Handler - Similar to WordPress media upload
 */
export class ImageUploader {
  constructor(options = {}) {
    this.options = {
      maxSize: 5 * 1024 * 1024, // 5MB
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      quality: 0.8,
      maxWidth: 1920,
      maxHeight: 1080,
      ...options
    };
  }

  validate(file) {
    const errors = [];

    if (file.size > this.options.maxSize) {
      errors.push(`File size must be less than ${this.options.maxSize / (1024 * 1024)}MB`);
    }

    if (!this.options.allowedTypes.includes(file.type)) {
      errors.push(`File type ${file.type} is not allowed`);
    }

    return errors;
  }

  async process(file) {
    const errors = this.validate(file);
    if (errors.length > 0) {
      throw new Error(errors.join(', '));
    }

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > this.options.maxWidth || height > this.options.maxHeight) {
          const ratio = Math.min(
            this.options.maxWidth / width,
            this.options.maxHeight / height
          );
          width *= ratio;
          height *= ratio;
        }

        // Set canvas size
        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          },
          file.type,
          this.options.quality
        );
      };

      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }
}

/**
 * Link Validator - Similar to WordPress link validation
 */
export class LinkValidator {
  static protocols = ['http:', 'https:', 'mailto:', 'tel:', 'ftp:'];

  static validate(url) {
    if (!url) return false;

    try {
      const parsed = new URL(url.startsWith('//') ? 'https:' + url : url);
      return this.protocols.includes(parsed.protocol);
    } catch {
      // Check for relative URLs
      return /^\/[^\/]/.test(url) || /^[^\/]*\.html?$/.test(url);
    }
  }

  static sanitize(url) {
    if (!url) return '';
    
    // Remove javascript: and other dangerous protocols
    if (/^javascript:/i.test(url)) return '';
    if (/^data:/i.test(url)) return '';
    if (/^vbscript:/i.test(url)) return '';
    
    return url.trim();
  }

  static makeAbsolute(url, baseUrl = window.location.origin) {
    if (!url) return '';
    
    try {
      return new URL(url, baseUrl).href;
    } catch {
      return url;
    }
  }
}

/**
 * Content Sanitizer - Similar to WordPress content filtering
 */
export class ContentSanitizer {
  static allowedTags = [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'strike', 'del',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'blockquote', 'pre', 'code',
    'a', 'img',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'div', 'span'
  ];

  static allowedAttributes = {
    'a': ['href', 'title', 'target', 'rel'],
    'img': ['src', 'alt', 'title', 'width', 'height'],
    'table': ['width', 'height'],
    'td': ['colspan', 'rowspan'],
    'th': ['colspan', 'rowspan'],
    '*': ['style', 'class', 'id']
  };

  static sanitize(html) {
    if (!html) return '';

    // Create a temporary div to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Recursively clean elements
    this.cleanElement(temp);

    return temp.innerHTML;
  }

  static cleanElement(element) {
    const children = Array.from(element.children);
    
    children.forEach(child => {
      const tagName = child.tagName.toLowerCase();
      
      // Remove disallowed tags
      if (!this.allowedTags.includes(tagName)) {
        child.replaceWith(...child.childNodes);
        return;
      }

      // Clean attributes
      const allowedAttrs = [
        ...(this.allowedAttributes[tagName] || []),
        ...(this.allowedAttributes['*'] || [])
      ];

      Array.from(child.attributes).forEach(attr => {
        if (!allowedAttrs.includes(attr.name)) {
          child.removeAttribute(attr.name);
        }
      });

      // Sanitize href attributes
      if (child.hasAttribute('href')) {
        const href = LinkValidator.sanitize(child.getAttribute('href'));
        if (href) {
          child.setAttribute('href', href);
        } else {
          child.removeAttribute('href');
        }
      }

      // Recursively clean children
      this.cleanElement(child);
    });
  }
}

/**
 * Keyboard Shortcuts Handler - Similar to WordPress keyboard shortcuts
 */
export class KeyboardShortcuts {
  constructor(editor) {
    this.editor = editor;
    this.shortcuts = new Map();
    this.setupDefaultShortcuts();
  }

  setupDefaultShortcuts() {
    // WordPress-style shortcuts
    this.add('cmd+b', () => this.editor.execCommand('Bold'));
    this.add('ctrl+b', () => this.editor.execCommand('Bold'));
    
    this.add('cmd+i', () => this.editor.execCommand('Italic'));
    this.add('ctrl+i', () => this.editor.execCommand('Italic'));
    
    this.add('cmd+u', () => this.editor.execCommand('Underline'));
    this.add('ctrl+u', () => this.editor.execCommand('Underline'));
    
    this.add('cmd+k', () => this.insertLink());
    this.add('ctrl+k', () => this.insertLink());
    
    this.add('cmd+shift+k', () => this.removeLink());
    this.add('ctrl+shift+k', () => this.removeLink());
  }

  add(shortcut, callback) {
    this.shortcuts.set(shortcut.toLowerCase(), callback);
  }

  remove(shortcut) {
    this.shortcuts.delete(shortcut.toLowerCase());
  }

  handleKeydown(event) {
    const key = this.getKeyString(event);
    const callback = this.shortcuts.get(key);
    
    if (callback) {
      event.preventDefault();
      callback();
    }
  }

  getKeyString(event) {
    const parts = [];
    
    if (event.ctrlKey) parts.push('ctrl');
    if (event.metaKey) parts.push('cmd');
    if (event.shiftKey) parts.push('shift');
    if (event.altKey) parts.push('alt');
    
    parts.push(event.key.toLowerCase());
    
    return parts.join('+');
  }

  insertLink() {
    const selection = this.editor.selection.getContent({ format: 'text' });
    const url = prompt('Enter URL:', 'https://');
    
    if (url) {
      if (selection) {
        this.editor.insertContent(`<a href="${url}">${selection}</a>`);
      } else {
        this.editor.insertContent(`<a href="${url}">${url}</a>`);
      }
    }
  }

  removeLink() {
    this.editor.execCommand('Unlink');
  }
}

/**
 * WordPress Advanced Editor Tools - Font Size Helper
 */
export class FontSizeHelper {
  constructor() {
    this.fontSizeFormats = '8px 10px 12px 14px 16px 20px 24px 28px 32px 36px 48px 60px 72px 96px';
    this.currentFontSize = '14px';
  }

  // Parse font size formats như WordPress Advanced Editor Tools
  getFontSizeList() {
    return this.fontSizeFormats.split(' ').map(size => ({
      text: size,
      value: size
    }));
  }

  // Apply font size như WordPress Advanced Editor Tools
  applyFontSize(editor, size) {
    if (!editor || !size) return;

    const selection = editor.selection;
    const content = selection.getContent();
    
    if (content) {
      // Wrap selected content with span
      const wrappedContent = `<span style="font-size: ${size};">${content}</span>`;
      editor.selection.setContent(wrappedContent);
    } else {
      // Set font size for current position
      editor.formatter.apply('fontsize', { value: size });
    }
    
    this.currentFontSize = size;
  }

  // Get current font size từ selection
  getCurrentFontSize(editor) {
    if (!editor) return '14px';

    const node = editor.selection.getNode();
    const computedStyle = window.getComputedStyle(node);
    return computedStyle.fontSize || '14px';
  }

  // WordPress Advanced Editor Tools font size registration
  registerFontSizeFormats(editor) {
    if (!editor || !editor.formatter) return;

    // Register all font sizes
    this.getFontSizeList().forEach(({ value }) => {
      editor.formatter.register(`fontsize_${value}`, {
        inline: 'span',
        styles: { 'font-size': value }
      });
    });

    console.log('WordPress Advanced Editor Tools: Font size formats registered');
  }
}

/**
 * Export all utilities
 */
export default {
  WordCounter,
  AutoSave,
  ImageUploader,
  LinkValidator,
  ContentSanitizer,
  KeyboardShortcuts,
  FontSizeHelper
}; 